import { Clock, Check } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { IconActionButton } from '@/components/ui/icon-action-button'
import type { AppNotificationRecord } from '@/types/notifications'
import { getNotificationSemantic } from '../lib/notification-semantic'
import { resolveNotificationActionUrl } from '../lib/notification-router'
import { getLocalizedNotification } from '../lib/notification-content'
import { useRelativeTime } from '../lib/relative-time'

interface NotificationListItemProps {
  item: AppNotificationRecord
  onMarkRead?: () => void
  isBusy?: boolean
}

export function NotificationListItem({ item, onMarkRead, isBusy }: NotificationListItemProps) {
  const { t } = useTranslation()
  const semantic = getNotificationSemantic(item)
  const actionUrl = resolveNotificationActionUrl(item)
  const { title, message } = getLocalizedNotification(item, t)
  const Icon = semantic.icon

  const isUnread = !item.read_at

  const timeLabel = useRelativeTime(item.created_at)

  return (
    <div className="group flex items-start gap-3 px-4 py-3.5 bg-card">
      {/* Icon / Avatar */}
      <div
        className={cn(
          'mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full',
          semantic.mediaClass
        )}
      >
        <Icon className={cn('size-4', semantic.iconClass)} />
      </div>

      {/* Content */}
      <div className="min-w-0 flex-1">
        <p className="text-sm leading-snug">
          {title}
        </p>
        <p className="mt-0.5 text-sm text-muted-foreground leading-snug">
          {message}
        </p>
        <div className="mt-1.5 flex items-center gap-2">
          <span className="text-[11px] text-muted-foreground/70 flex items-center gap-1">
            <Clock className="size-3" />
            {timeLabel}
          </span>
          {isUnread && (
            <span className="inline-flex items-center rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">
              {t('notifications.page.new')}
            </span>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex shrink-0 items-start gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
        {actionUrl && (
          <IconActionButton
            icon={
              <svg className="size-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
              </svg>
            }
            tooltip={t('notifications.page.view')}
            to={actionUrl}
          />
        )}

        {isUnread && onMarkRead && (
          <IconActionButton
            icon={<Check className="size-4" />}
            tooltip={t('notifications.page.read')}
            tone="success"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              onMarkRead()
            }}
            disabled={isBusy}
          />
        )}
      </div>
    </div>
  )
}
